import type { AuthStep } from '@dreamstack/auth'
import { Css_HeadingH2_tw, Text_20_30 } from '@dreamstack/feature-components/src/layout/Texts'
import { AuthBackButton } from '@dreamstack/feature-components/src/auth/modal/AuthBackButton'
import { cn } from '@dreamstack/util/src/cn'
import { Dialog, DialogBackdrop } from '@headlessui/react'
import type { FunctionComponent } from 'react'
import { ModalCloseButton } from './ModalCloseButton'

const DialogContent = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn('mt-px-16 w-full', className)} {...rest}>
    {children}
  </div>
)

const DialogTitle = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <h2 className={cn(Css_HeadingH2_tw, 'font-semibold', className)} {...rest}>
    {children}
  </h2>
)

const ModalWrapper = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div
    className={cn(
      'max-h-[90vh] overflow-y-auto p-px-24 pt-px-40 mx-px-24 sm:mx-auto sm:p-px-40 sm:pt-px-64 md:w-[540px] md:max-w-[540px] ml:p-px-64 ml:pt-px-80 ml:w-[680px] ml:max-w-[680px] relative bg-accentroWhite-full max-w-[400px] w-full',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

const IntroWrapper = ({
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ className?: string }>) => (
  <div className={cn('mb-px-40 ml:mb-px-48', className)} {...rest}>
    {children}
  </div>
)

export type ModalProps = {
  open?: boolean
  title: string
  handleClose: CallableFunction
  goBackTo?: AuthStep
  intro?: string
}

export const SimpleModal: FunctionComponent<
  React.PropsWithChildren<ModalProps>
> = ({ open, handleClose, title, goBackTo, children, intro }) => {
  return open ? (
    <Dialog
      open={open}
      onClose={() => handleClose()}
      aria-labelledby="form-dialog-title"
      className="fixed z-30 inset-0 overflow-y-auto flex items-center justify-center"
    >
      <DialogBackdrop className="fixed inset-0 backdrop-blur-sm bg-accentroGray-300" />
      <ModalWrapper>
        <ModalCloseButton handleClose={handleClose} />
        <IntroWrapper>
          <div className="flex mb-px-16 items-center">
            {goBackTo && <AuthBackButton toStep={goBackTo} />}
            <DialogTitle>{title}</DialogTitle>
          </div>
          {!!intro && <Text_20_30>{intro}</Text_20_30>}
        </IntroWrapper>
        <DialogContent>{children}</DialogContent>
      </ModalWrapper>
    </Dialog>
  ) : null
}
